// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// 86cb8uk5f (ticket #1028 load work): what useAutoRefresh asks before each background tick.
//
//  * LEADER — several visible tabs/windows (multi-monitor pharmacy desks) polling the same
//    endpoint for the same identity multiplied every interval. Each poll on a keyed refresh is
//    announced over a BroadcastChannel; a tab that has heard a fresh announcement from a tab with
//    a LOWER id is a follower for that key. Followers SLOW DOWN rather than stop, so a window the
//    user is looking at never goes stale for long; the lowest id never defers, so there is always
//    one full-rate poller and two tabs can never both defer. No BroadcastChannel → nobody is a
//    follower, i.e. exactly the old per-tab behaviour.
//  * IDLE — no keyboard / pointer / visibility input for IDLE_AFTER_MS slows every tick the same
//    way; the next input snaps back.
//
// Keys MUST carry the identity the data belongs to (account / pharmacy): sessions are per-tab, so
// two tabs can be different users, and one must never defer to the other's poll.

export const IDLE_AFTER_MS = 5 * 60_000
/** Interval multiplier while idle or following. */
export const SLOW_FACTOR = 4

let tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
const heard = new Map<string, Map<string, number>>()   // key -> announcing tab id -> last poll at
let channel: BroadcastChannel | null | undefined
let lastInput = Date.now()
let inputWired = false

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel
  try {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('owlrx-refresh-leader')
    if (channel) channel.onmessage = (e: MessageEvent) => noteAnnouncement(e.data)
  } catch { channel = null }
  return channel
}

/** Record another tab's poll. Exported for tests. */
export function noteAnnouncement(msg: unknown): void {
  const m = msg as { key?: unknown; id?: unknown; at?: unknown } | null
  if (typeof m?.key !== 'string' || typeof m.id !== 'string' || typeof m.at !== 'number' || m.id === tabId) return
  let byTab = heard.get(m.key)
  if (!byTab) heard.set(m.key, byTab = new Map())
  byTab.set(m.id, m.at)
}

/** Tell the other tabs this one just polled `key`. */
export function announce(key: string): void {
  try { getChannel()?.postMessage({ key, id: tabId, at: Date.now() }) } catch { /* best-effort */ }
}

/** True when a lower-id tab polled `key` within `freshMs`. */
export function isFollower(key: string, freshMs: number, now = Date.now()): boolean {
  getChannel()
  const byTab = heard.get(key)
  if (!byTab) return false
  for (const [id, at] of byTab) {
    if (now - at > freshMs) { byTab.delete(id); continue }   // closed or hidden tab: forget it
    if (id < tabId) return true
  }
  return false
}

function wireInput(): void {
  if (inputWired || typeof window === 'undefined' || typeof document === 'undefined') return
  inputWired = true
  const mark = () => { lastInput = Date.now() }
  for (const ev of ['keydown', 'pointerdown', 'pointermove', 'wheel', 'touchstart'])
    window.addEventListener(ev, mark, { passive: true, capture: true })
  document.addEventListener('visibilitychange', mark)
}

/** Record user input (the window listeners call this path too). Exported for tests. */
export function markInput(now = Date.now()): void { lastInput = now }

export function isIdle(now = Date.now()): boolean {
  wireInput()
  return now - lastInput >= IDLE_AFTER_MS
}

/** Tests only: forget every announcement, reset the idle clock, optionally pin this tab's id. */
export function __resetCoordinator(id?: string): void {
  heard.clear()
  lastInput = Date.now()
  if (id !== undefined) tabId = id
}
